import React, { useEffect } from 'react'
import { subtract, isFinite, vn } from 'vend-number'
import { Button, Icon } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { createSelector } from '@reduxjs/toolkit'
import { useFormatCurrencyValue } from '../../summary-panel/useFormatCurrencyValue'
import {
  getOutstandingBalance,
  isAccountBalancePaymentAdded,
} from '../../../../functions/sale'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import { RootState } from '../../../../state/store'
import { updatePayAccountBalanceAmount } from '../../../../state/slices/currentSale/currentSaleSlice'
import { peekPostPaymentRedirectUrl } from '../../../../util/post-payment-redirect'

const AMOUNT_TENDERED_CONTEXT = 'Message for amount tendered'
const MAX_TENDERED_AMOUNT = 10_000_000_000

const dataSelector = createSelector(
  [
    (state: RootState) => state.currentSale.originalSalePayments,
    (state: RootState) => state.currentSale.payments,
    (state: RootState) => state.currentSale.returnFor,
    (state: RootState) => state.currentSale.finals,
    (state: RootState) => state.currentSale.payAccountBalance,
    (state: RootState) => state.currentSale.customerBalances,
  ],
  (
    originalSalePayments,
    payments,
    returnFor,
    finals,
    payAccountBalance,
    customerBalances
  ) => ({
    originalSalePayments,
    payments,
    returnFor,
    finals,
    payAccountBalance,
    customerBalances,
  })
)

export const useAmountTenderedMessages = (
  amountTendered: string,
  setAmountTendered: (amount: string) => void
) => {
  const dispatch = useAppDispatch()
  const saleData = useAppSelector(dataSelector)
  const formatCurrencyValue = useFormatCurrencyValue()
  const isAccountBalanceFlow = peekPostPaymentRedirectUrl() !== null

  // Maximum the merchant can apply to the balance — the customer's full outstanding debt.
  const maxBalance = Math.abs(saleData.customerBalances?.account ?? 0)

  // When in the account balance flow, keep the ABP amount in sync with whatever
  // the merchant types in the Pay input. Cap at the customer's actual balance so
  // we never send more than is owed to the monolith. Also keep the sale note in
  // sync so reports reflect the actual amount processed, not the initial balance.
  //
  // formatCurrencyValue is intentionally omitted from deps: useFormatCurrencyValue
  // returns a new function reference on every render (no useCallback). The output is
  // stable since retailer settings don't change mid-session, so capturing it on the
  // render triggered by amountTendered changing is always correct.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isAccountBalanceFlow) return
    // Wait for customerBalances to load before capping. Without this guard,
    // maxBalance is 0 (from the ?? 0 fallback) and cappedAmount would be
    // Math.min(amount, 0) = 0, silently zeroing the payment amount.
    if (!saleData.customerBalances) return
    const amount = Number(amountTendered)
    if (!Number.isFinite(amount) || amount <= 0) return
    const cappedAmount = Math.min(amount, maxBalance)
    dispatch(updatePayAccountBalanceAmount(cappedAmount))
  }, [
    amountTendered,
    dispatch,
    isAccountBalanceFlow,
    maxBalance,
    saleData.customerBalances,
  ])

  const currentBalance = getOutstandingBalance(saleData)
  const remainingBalance =
    isFinite(currentBalance) && isFinite(amountTendered)
      ? subtract(currentBalance, amountTendered)
      : 0

  const getErrorMessage = () => {
    if (
      amountTendered === '' ||
      amountTendered === undefined ||
      amountTendered === null
    ) {
      return (
        <span className="payment-form-error-message vd-colour-no">
          <T _str="Please enter an amount" _context={AMOUNT_TENDERED_CONTEXT} />
        </span>
      )
    }

    if (isNaN(Number(amountTendered))) {
      return (
        <span className="payment-form-error-message vd-colour-no">
          <T _str="Invalid character" _context={AMOUNT_TENDERED_CONTEXT} />
        </span>
      )
    }

    if (Number(amountTendered) > MAX_TENDERED_AMOUNT) {
      return (
        <span className="payment-form-error-message vd-colour-no">
          <T
            _str="Amount must be less than {amount}"
            _context={AMOUNT_TENDERED_CONTEXT}
            amount={formatCurrencyValue(MAX_TENDERED_AMOUNT)}
          />
        </span>
      )
    }

    // In the account balance flow the Pay input directly controls the ABP amount,
    // so the "must be paid in full" guard is irrelevant — suppress it.
    if (!isAccountBalanceFlow) {
      const accountBalanceAdded = isAccountBalancePaymentAdded(
        saleData.payAccountBalance
      )
      const balanceAmount = vn(saleData.payAccountBalance?.amount)

      if (accountBalanceAdded && balanceAmount.gt(amountTendered)) {
        return (
          <div className="vd-flex">
            <Icon icon="fa-exclamation-triangle" className="vd-mt1 vd-mr1" />
            <span>
              <T
                _str="The Account Balance Payment must be paid in full."
                _context="Message when tendered amount is less than account balance"
              />{' '}
              <br />
              <Button
                link
                variant="supplementary"
                onClick={() => setAmountTendered(balanceAmount.toString())}
                data-track="change-amount-to"
              >
                <T
                  _str="Change amount to {balanceAmount}"
                  balanceAmount={formatCurrencyValue(balanceAmount.toString())}
                  _context="Button to increase payment amount to account balance"
                />
              </Button>
            </span>
          </div>
        )
      }
    }
  }

  const getMessage = () => {
    if (remainingBalance > 0 && currentBalance > 0) {
      return (
        <span className="payment-form-message">
          <T
            _str="{remainingBalance} left to pay"
            remainingBalance={formatCurrencyValue(remainingBalance)}
            _context={AMOUNT_TENDERED_CONTEXT}
          />
        </span>
      )
    }

    if (remainingBalance === 0 && currentBalance > 0) {
      return (
        <span className="payment-form-message">
          <T
            _str="Edit to make a partial payment"
            _context={AMOUNT_TENDERED_CONTEXT}
          />
        </span>
      )
    }

    if (remainingBalance < 0 && currentBalance < 0) {
      return (
        <span className="payment-form-message">
          <T
            _str="{remainingBalance} left to refund"
            remainingBalance={formatCurrencyValue(remainingBalance)}
            _context={AMOUNT_TENDERED_CONTEXT}
          />
        </span>
      )
    }

    if (remainingBalance === 0 && currentBalance < 0) {
      return (
        <span className="payment-form-message">
          <T
            _str="Edit to make a partial refund"
            _context={AMOUNT_TENDERED_CONTEXT}
          />
        </span>
      )
    }
  }

  return { error: getErrorMessage(), message: getMessage() }
}
